import { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { AnimatePresence, motion } from "framer-motion";
import {
  ChevronLeft, ChevronRight, Loader2, AlertTriangle,
  ZoomIn, ZoomOut, Maximize2, MoveHorizontal, BookOpen, Minus,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Worker pinned to the installed pdf.js version (avoids CRA worker bundling issues).
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DEFAULT_ASPECT = 8.5 / 11; // width / height, until the real ratio is known
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;

const Spinner = ({ label }) => (
  <div className="flex h-full min-h-[200px] w-full items-center justify-center text-muted-foreground">
    <Loader2 className="mr-2 animate-spin" size={18} /> {label || "Loading…"}
  </div>
);

export function PdfFlipReader({ file }) {
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [dir, setDir] = useState(1);
  const [stage, setStage] = useState({ w: 640, h: 520 });
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);
  const [fit, setFit] = useState("page"); // "page" | "width"
  const [zoom, setZoom] = useState(1);
  const [spread, setSpread] = useState(false);
  const [spreadTouched, setSpreadTouched] = useState(false);
  const [flip, setFlip] = useState(null); // null | "next" | "prev" (spread page-turn in progress)
  const [error, setError] = useState(false);
  const roRef = useRef(null);
  const spreadTouchedRef = useRef(false);

  // Attach the ResizeObserver via a callback ref so it binds when the stage node
  // actually mounts (react-pdf swaps in children only after the PDF loads).
  const stageCbRef = useCallback((node) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!node) return;
    const ro = new ResizeObserver(() => {
      const w = node.clientWidth, h = node.clientHeight;
      if (w && h) {
        setStage({ w, h });
        if (!spreadTouchedRef.current) setSpread(w >= 760);
      }
    });
    ro.observe(node);
    roRef.current = ro;
  }, []);

  const cols = spread ? 2 : 1;

  // Base (fit) width per page, then scaled by zoom.
  const padX = 48, padY = 44, gap = spread ? 16 : 0;
  const availW = Math.max(160, (stage.w - padX - gap) / cols);
  const fitByHeight = Math.max(160, (stage.h - padY) * aspect);
  const baseWidth = fit === "width" ? availW : Math.min(availW, fitByHeight);
  const pageWidth = Math.round(baseWidth * zoom);

  const snap = useCallback((p) => (spread ? (p % 2 === 0 ? p - 1 : p) : p), [spread]);

  const go = useCallback((next) => {
    setPage((p) => {
      const target = snap(Math.min(numPages || 1, Math.max(1, next)));
      setDir(target >= p ? 1 : -1);
      return target;
    });
  }, [numPages, snap]);

  const reducedMotion = typeof window !== "undefined" &&
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const canNext = page + cols - 1 < numPages;
  const canPrev = page > 1;

  // Next/Prev + arrows: in spread mode (and motion allowed) do the 3D page-curl; otherwise jump instantly.
  const turn = useCallback((type) => {
    if (flip) return;
    if (type === "next" && !canNext) return;
    if (type === "prev" && !canPrev) return;
    if (!spread || reducedMotion) {
      go(type === "next" ? page + cols : page - cols);
      return;
    }
    setDir(type === "next" ? 1 : -1);
    setFlip(type);
  }, [flip, canNext, canPrev, spread, reducedMotion, go, page, cols]);

  // Re-snap current page when toggling spread so pairs stay aligned.
  useEffect(() => { setPage((p) => snap(p)); }, [spread, snap]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") turn("next");
      else if (e.key === "ArrowLeft") turn("prev");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [turn]);

  const setZoomClamped = (z) => setZoom(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round(z * 100) / 100)));

  if (error) {
    return (
      <div className="flex h-[72vh] flex-col items-center justify-center gap-3 bg-muted text-center text-muted-foreground">
        <AlertTriangle size={22} className="text-[hsl(var(--processor-warning))]" />
        <p className="max-w-sm text-sm">This volume couldn't be previewed inline. Use “Open in new tab” to read the full PDF.</p>
      </div>
    );
  }

  const visible = spread ? [page, page + 1].filter((n) => n <= numPages) : [page];

  const pageH = Math.round(pageWidth / aspect);
  // One white page surface (blank if out of range). Captures the real aspect ratio from page 1.
  const PageFace = (num, rounded = "rounded-sm") => (
    <div className={`overflow-hidden bg-white ring-1 ring-black/10 ${rounded}`} style={{ width: pageWidth, height: pageH }}>
      {num >= 1 && num <= numPages && (
        <Page
          pageNumber={num}
          width={pageWidth}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onLoadSuccess={(p) => { const r = p.width / p.height; setAspect((prev) => (Math.abs(prev - r) > 0.002 ? r : prev)); }}
          loading={<div style={{ width: pageWidth, height: pageH }}><Spinner label="" /></div>}
        />
      )}
    </div>
  );

  // Thumbnail filmstrip window around the current page.
  const start = Math.max(1, page - 4);
  const end = Math.min(numPages, page + 5);
  const thumbs = [];
  for (let i = start; i <= end; i++) thumbs.push(i);

  const CtrlBtn = ({ onClick, disabled, active, children, ...rest }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex h-8 w-8 items-center justify-center rounded-sm border transition-colors disabled:pointer-events-none disabled:opacity-30 ${
        active ? "border-[hsl(var(--teal))] bg-[hsl(var(--teal)/0.12)] text-[hsl(var(--teal))]" : "border-border text-foreground hover:bg-secondary"
      }`}
      {...rest}
    >
      {children}
    </button>
  );

  return (
    <Document
      file={file}
      onLoadSuccess={({ numPages: n }) => { setNumPages(n); setError(false); }}
      onLoadError={() => setError(true)}
      loading={<div className="h-[72vh]"><Spinner label="Opening volume…" /></div>}
      error={<div className="h-[72vh]"><Spinner label="" /></div>}
      className="flex h-[72vh] flex-col bg-muted"
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-center gap-2 border-b border-border bg-card px-4 py-2">
        <div className="flex items-center gap-1">
          <CtrlBtn onClick={() => setZoomClamped(zoom - 0.25)} disabled={zoom <= ZOOM_MIN} aria-label="Zoom out" data-testid="reader-zoom-out"><ZoomOut size={15} /></CtrlBtn>
          <button
            onClick={() => setZoom(1)}
            className="min-w-[52px] rounded-sm border border-border px-2 py-1 font-mono text-[11px] text-foreground hover:bg-secondary"
            data-testid="reader-zoom-reset"
            title="Reset zoom"
          >
            {Math.round(zoom * 100)}%
          </button>
          <CtrlBtn onClick={() => setZoomClamped(zoom + 0.25)} disabled={zoom >= ZOOM_MAX} aria-label="Zoom in" data-testid="reader-zoom-in"><ZoomIn size={15} /></CtrlBtn>
        </div>

        <span className="mx-1 h-5 w-px bg-border" />

        <div className="flex items-center gap-1">
          <CtrlBtn onClick={() => { setFit("width"); setZoom(1); }} active={fit === "width"} aria-label="Fit width" data-testid="reader-fit-width" title="Fit width"><MoveHorizontal size={15} /></CtrlBtn>
          <CtrlBtn onClick={() => { setFit("page"); setZoom(1); }} active={fit === "page"} aria-label="Fit page" data-testid="reader-fit-page" title="Fit page"><Maximize2 size={15} /></CtrlBtn>
        </div>

        <span className="mx-1 h-5 w-px bg-border" />

        <CtrlBtn
          onClick={() => { spreadTouchedRef.current = true; setSpreadTouched(true); setSpread((s) => !s); }}
          active={spread}
          aria-label="Toggle two-page spread"
          data-testid="reader-spread-toggle"
          title={spread ? "Single page" : "Two-page spread"}
        >
          {spread ? <Minus size={15} /> : <BookOpen size={15} />}
        </CtrlBtn>
      </div>

      {/* Page stage */}
      <div ref={stageCbRef} className="relative flex flex-1 items-center justify-center overflow-auto px-4 py-5">
        <button
          onClick={() => turn("prev")}
          disabled={page <= 1 || !!flip}
          aria-label="Previous page"
          data-testid="reader-prev"
          className="sticky left-0 z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex flex-1 items-center justify-center">
          {spread ? (
            <div className="relative flex items-center justify-center" style={{ perspective: 2400 }} data-testid="reader-stage">
              <div className="relative" style={{ width: pageWidth * 2, height: pageH, transformStyle: "preserve-3d" }}>
                {flip ? (
                  <>
                    {/* Pages revealed beneath the turning leaf */}
                    {flip === "next" ? (
                      <>
                        <div className="absolute left-0 top-0">{PageFace(page, "rounded-l-sm")}</div>
                        <div className="absolute top-0" style={{ left: pageWidth }}>{PageFace(page + 3, "rounded-r-sm")}</div>
                      </>
                    ) : (
                      <>
                        <div className="absolute left-0 top-0">{PageFace(page - 2, "rounded-l-sm")}</div>
                        <div className="absolute top-0" style={{ left: pageWidth }}>{PageFace(page + 1, "rounded-r-sm")}</div>
                      </>
                    )}
                    {/* The turning leaf (two faces), pivoting at the spine */}
                    <motion.div
                      key={`leaf-${flip}-${page}`}
                      className="absolute top-0"
                      style={{
                        width: pageWidth, height: pageH, top: 0,
                        left: flip === "next" ? pageWidth : 0,
                        transformOrigin: flip === "next" ? "left center" : "right center",
                        transformStyle: "preserve-3d",
                        zIndex: 5,
                      }}
                      initial={{ rotateY: 0 }}
                      animate={{ rotateY: flip === "next" ? -180 : 180 }}
                      transition={{ duration: 0.72, ease: [0.645, 0.045, 0.355, 1] }}
                      onAnimationComplete={() => {
                        setPage((p) => (flip === "next" ? Math.min(numPages, p + 2) : Math.max(1, p - 2)));
                        setFlip(null);
                      }}
                    >
                      <div className="absolute inset-0 shadow-xl" style={{ backfaceVisibility: "hidden" }}>
                        {PageFace(flip === "next" ? page + 1 : page, flip === "next" ? "rounded-r-sm" : "rounded-l-sm")}
                        <div className="pointer-events-none absolute inset-0" style={{ background: flip === "next" ? "linear-gradient(to left, rgba(0,0,0,0.20), transparent 42%)" : "linear-gradient(to right, rgba(0,0,0,0.20), transparent 42%)" }} />
                      </div>
                      <div className="absolute inset-0 shadow-xl" style={{ backfaceVisibility: "hidden", transform: "rotateY(180deg)" }}>
                        {PageFace(flip === "next" ? page + 2 : page - 1, flip === "next" ? "rounded-l-sm" : "rounded-r-sm")}
                        <div className="pointer-events-none absolute inset-0" style={{ background: flip === "next" ? "linear-gradient(to right, rgba(0,0,0,0.20), transparent 42%)" : "linear-gradient(to left, rgba(0,0,0,0.20), transparent 42%)" }} />
                      </div>
                    </motion.div>
                  </>
                ) : (
                  <>
                    <div className="absolute left-0 top-0 shadow-xl">{PageFace(page, "rounded-l-sm")}</div>
                    {page + 1 <= numPages && (
                      <div className="absolute top-0 shadow-xl" style={{ left: pageWidth }}>{PageFace(page + 1, "rounded-r-sm")}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          ) : (
            <AnimatePresence mode="wait" custom={dir}>
              <motion.div
                key={page}
                custom={dir}
                initial={{ opacity: 0, x: dir * 40, rotateY: dir * 4 }}
                animate={{ opacity: 1, x: 0, rotateY: 0 }}
                exit={{ opacity: 0, x: dir * -40, rotateY: dir * -4 }}
                transition={{ duration: 0.28, ease: "easeInOut" }}
                style={{ transformPerspective: 1400 }}
                className="flex items-stretch shadow-xl"
                data-testid="reader-stage"
              >
                {PageFace(page, "rounded-sm")}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        <button
          onClick={() => turn("next")}
          disabled={page + cols - 1 >= numPages || !!flip}
          aria-label="Next page"
          data-testid="reader-next"
          className="sticky right-0 z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-background/90 text-foreground shadow-sm transition-colors hover:bg-secondary disabled:pointer-events-none disabled:opacity-30"
        >
          <ChevronRight size={18} />
        </button>
      </div>

      {/* Page controls */}
      <div className="flex items-center justify-center gap-3 border-t border-border bg-card px-4 py-2.5">
        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => turn("prev")} disabled={page <= 1 || !!flip} data-testid="reader-prev-btn">
          <ChevronLeft size={15} className="mr-1" /> Prev
        </Button>
        <div className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground" data-testid="reader-page-indicator">
          <input
            type="number"
            min={1}
            max={numPages || 1}
            value={page}
            onChange={(e) => go(parseInt(e.target.value, 10) || 1)}
            className="w-14 rounded-sm border border-border bg-background px-2 py-1 text-center text-foreground"
            data-testid="reader-page-input"
          />
          <span>{spread && visible.length === 2 ? `– ${visible[1]} ` : ""}/ {numPages || "…"}</span>
        </div>
        <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => turn("next")} disabled={page + cols - 1 >= numPages || !!flip} data-testid="reader-next-btn">
          Next <ChevronRight size={15} className="ml-1" />
        </Button>
      </div>

      {/* Thumbnail filmstrip */}
      {numPages > 1 && (
        <div className="flex items-center gap-2 overflow-x-auto border-t border-border bg-secondary/40 px-4 py-3" data-testid="reader-thumbnails">
          {start > 1 && <span className="shrink-0 font-mono text-[10px] text-muted-foreground">1 …</span>}
          {thumbs.map((i) => {
            const activeThumb = visible.includes(i);
            return (
              <button
                key={i}
                onClick={() => go(i)}
                data-testid={`reader-thumb-${i}`}
                className={`group shrink-0 overflow-hidden rounded-sm border transition-colors ${
                  activeThumb ? "border-[hsl(var(--matcha))] ring-1 ring-[hsl(var(--matcha))]" : "border-border hover:border-[hsl(var(--teal))]"
                }`}
                title={`Page ${i}`}
              >
                <Page
                  pageNumber={i}
                  width={72}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  loading={<div style={{ width: 72, height: 94 }} className="flex items-center justify-center bg-card"><Loader2 size={12} className="animate-spin text-muted-foreground" /></div>}
                />
                <div className={`py-0.5 text-center font-mono text-[9px] ${activeThumb ? "bg-[hsl(var(--matcha))] text-[hsl(var(--matcha-foreground))]" : "bg-card text-muted-foreground"}`}>
                  {i}
                </div>
              </button>
            );
          })}
          {end < numPages && <span className="shrink-0 font-mono text-[10px] text-muted-foreground">… {numPages}</span>}
        </div>
      )}
    </Document>
  );
}
